import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { CreditCard, Shield, Zap, ArrowRight, Globe, TrendingUp, CheckCircle } from 'lucide-react'
import { motion } from 'framer-motion'

export default function Home() {
  const { isAuthenticated } = useAuthStore()

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  }

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, delay: 0.2 },
    },
  }

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, delay: 0.4 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  }

  return (
    <motion.div className="min-h-screen bg-gray-900" initial="hidden" animate="visible">
      <motion.section
        className="relative px-4 py-10 sm:py-30 lg:py-10"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <div className="px-4 mx-auto max-w-7xl">
          <div className="grid items-center order-1 gap-12 lg:grid-cols-2">
            {/* Left Content */}
            <motion.div className="text-white" variants={fadeInLeft}>
              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                Instant Card-to-Crypto <span className="text-orange-400">Payments</span>
                {/* for <span className="text-orange-400">High-Risk</span> Merchants */}
              </h1>
              <p className="max-w-2xl mb-8 text-gray-300">
                Our platform is built for businesses that demand reliability and protection. With no
                chargebacks, merchants can accept payments confidently, free from the risks and
                disputes common in traditional payment systems.
              </p>
              <div className="flex flex-col gap-4 mb-8 sm:flex-row">
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600"
                    >
                      Get Started
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </>
                )}
              </div>
            </motion.div>

            {/* Right Visual - 3D Safe with Coins */}
            <motion.div className="relative h-full" variants={fadeInRight}>
              <div className="relative h-full p-8 bg-center bg-cover rounded-2xl sm:h-full">
                <div className="absolute inset-0 "></div>
                <div className="relative z-10 flex flex-col justify-center text-center sm:h-full">
                  <img
                    src="/cripton.png"
                    alt="Customer Experience"
                    className="w-full rounded-3xl"
                  />
                </div>
                {/* Floating coins effect */}
                <div className="absolute w-8 h-8 bg-yellow-400 rounded-full top-4 right-4 animate-bounce"></div>
                <div className="absolute w-6 h-6 bg-orange-400 rounded-full bottom-8 left-8 animate-pulse"></div>
                <div className="absolute w-4 h-4 bg-yellow-300 rounded-full top-1/2 right-8 animate-ping"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Payment Dashboard Section */}
      <motion.section
        className="px-4 py-20 bg-gray-900 border-t border-gray-900"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        <div className="mx-auto max-w-7xl">
          <motion.div className="" variants={fadeInUp}>
            <h2 className="mb-4 text-5xl font-bold text-center text-pexi-orange">Who We Serve</h2>
            <p className="max-w-[70%] mx-auto mb-12 text-xl font-light text-center text-white">
              We deliver outstanding results while streamlining your processes. Our goal is to
              maximize efficiency and ensure you achieve the best outcomes with minimal effort and
              expense.
            </p>
            <motion.div className="grid gap-6 md:grid-cols-3" variants={staggerContainer}>
              <motion.div
                className="p-8 text-center bg-gray-700 border border-gray-900 rounded-xl"
                variants={fadeInUp}
              >
                <img
                  src="/console.png"
                  alt="Gaming"
                  className="p-4 mx-auto my-4 rounded-xl bg-pexi-orange"
                  width={80}
                />
                <div className="mb-2 text-xl font-bold text-center text-pexi-orange">Gaming</div>
                <div className="mb-8 font-light text-center text-white ">
                  Accept payment form the gaming community in digital currencies. Get settled
                  immediately to your wallet.
                </div>
              </motion.div>

              <motion.div
                className="p-8 text-center bg-gray-700 border border-gray-900 rounded-xl"
                variants={fadeInUp}
              >
                <img
                  src="/travel-and-tourism.png"
                  alt="Gaming"
                  className="p-4 mx-auto my-4 rounded-xl bg-pexi-orange"
                  width={80}
                />
                <div className="mb-2 text-xl font-bold text-center text-pexi-orange">
                  Travel & Hospitality
                </div>
                <div className="mb-8 font-light text-center text-white">
                  Accept payment in digital currencies and Get settled immediately to your wallet.
                </div>
              </motion.div>

              <motion.div
                className="p-8 text-center bg-gray-700 border border-gray-900 rounded-xl"
                variants={fadeInUp}
              >
                <img
                  src="/e-wallet.png"
                  alt="Gaming"
                  className="p-4 mx-auto my-4 rounded-xl bg-pexi-orange"
                  width={80}
                />
                <div className="mb-2 text-xl font-bold text-center text-pexi-orange">
                  Fintech & Digital Wallet
                </div>
                <div className="mb-8 font-light text-center text-white">
                  Allow digital currency top ups and expand your platform’s reach to various digital
                  currency users globally.
                </div>
              </motion.div>

              <motion.div
                className="p-8 text-center bg-gray-700 border border-gray-900 rounded-xl"
                variants={fadeInUp}
              >
                <img
                  src="/exchange.png"
                  alt="Gaming"
                  className="p-4 mx-auto my-4 rounded-xl bg-pexi-orange"
                  width={80}
                />
                <div className="mb-2 text-xl font-bold text-center text-pexi-orange">
                  Payment Service Providers
                </div>
                <div className="font-light text-center text-white">
                  Enable your merchants to accept digital currencies and tap into various
                  cryptocurrency users. All without touching digital currencies.
                </div>
              </motion.div>

              <motion.div
                className="p-8 text-center bg-gray-700 border border-gray-900 rounded-xl"
                variants={fadeInUp}
              >
                <img
                  src="/shopping-cart.png"
                  alt="Gaming"
                  className="p-4 mx-auto my-4 rounded-xl bg-pexi-orange"
                  width={80}
                />
                <div className="mb-2 text-xl font-bold text-center text-pexi-orange">
                  E-Commerce & Marketplace
                </div>
                <div className="mb-8 font-light text-center text-white">
                  Accept payment in digital currencies and Get settled immediately to your wallet.
                  Add the option to pay your sellers in digital currencies.
                </div>
              </motion.div>

              <motion.div
                className="p-8 text-center bg-gray-700 border border-gray-900 rounded-xl"
                variants={fadeInUp}
              >
                <img
                  src="/blockchain.png"
                  alt="Gaming"
                  className="p-4 mx-auto my-4 rounded-xl bg-pexi-orange"
                  width={80}
                />
                <div className="mb-2 text-xl font-bold text-center text-pexi-orange">Web3</div>
                <div className="mb-8 font-light text-center text-white">
                  A Speed up global settlements in any currency. Receive and make global transfers
                  in traditional digital currencies.
                </div>
              </motion.div>
              {/* <div className="p-8 text-center"></div>
              <div className="p-8 text-center bg-gray-700 border border-gray-900 rounded-xl">
                <img src='/forex.png' alt='Gaming' className='p-4 mx-auto my-4 rounded-xl bg-pexi-orange' width={80} />
                <div className='mb-2 text-xl font-bold text-center text-pexi-orange'>International Exchange</div>
                <div className="mb-8 font-light text-center text-white">
                  Empower your merchants to accept payments from cryptocurrency users worldwide, without ever handling digital currencies directly.
                </div>
              </div>
              <div className="p-8 text-center"></div> */}
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Section */}
      <motion.section
        className="px-4 py-10"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        <div className="mx-auto max-w-7xl">
          <motion.h2
            className="mb-4 text-4xl font-bold text-center text-pexi-orange"
            variants={fadeInUp}
          >
            One integration, dual payment rails
          </motion.h2>
          <motion.p
            className="max-w-3xl mx-auto mb-16 text-xl font-light text-center text-gray-300"
            variants={fadeInUp}
          >
            Streamline your payment infrastructure while opening up new revenue streams
          </motion.p>
          <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
            <div className=" rounded-3xl bg-slate-800">
              <img src="/bank.png" alt="Integration" className="w-full p-12" />
            </div>
            <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
              <div className="p-8 text-center group bg-slate-800 rounded-3xl">
                <div className="flex justify-center mb-6">
                  <div className="p-4 transition-colors bg-orange-500/20 rounded-2xl group-hover:bg-orange-500/30">
                    <CreditCard className="w-12 h-12 text-orange-400" />
                  </div>
                </div>
                <h3 className="mb-4 text-xl font-semibold text-white">Card-to-Crypto Payments</h3>
                <p className="font-light text-gray-300">
                  Accept payments via credit cards, debit cards and bank transfers while settling in
                  your preferred cryptocurrency
                </p>
              </div>

              <div className="p-8 text-center group bg-slate-800 rounded-3xl">
                <div className="flex justify-center mb-6">
                  <div className="p-4 transition-colors bg-orange-500/20 rounded-2xl group-hover:bg-orange-500/30">
                    <Zap className="w-12 h-12 text-orange-400" />
                  </div>
                </div>
                <h3 className="mb-4 text-xl font-semibold text-white">Blazing Fast Settlements</h3>
                <p className="font-light text-gray-300">
                  Receive crypto payments in your wallet instantly after the transaction was
                  completed. (T+0)
                </p>
              </div>

              <div className="p-8 text-center group bg-slate-800 rounded-3xl">
                <div className="flex justify-center mb-6">
                  <div className="p-4 transition-colors bg-orange-500/20 rounded-2xl group-hover:bg-orange-500/30">
                    <Globe className="w-12 h-12 text-orange-400" />
                  </div>
                </div>
                <h3 className="mb-4 text-xl font-semibold text-white">Global Reach</h3>
                <p className="font-light text-gray-300">
                  Available in 150+ countries with support for major payment methods and 40+
                  blockchain networks
                </p>
              </div>

              <div className="p-8 text-center group bg-slate-800 rounded-3xl">
                <div className="flex justify-center mb-6">
                  <div className="p-4 transition-colors bg-orange-500/20 rounded-2xl group-hover:bg-orange-500/30">
                    <Shield className="w-12 h-12 text-orange-400" />
                  </div>
                </div>
                <h3 className="mb-4 text-xl font-semibold text-white">Industry-Leading Security</h3>
                <p className="font-light text-gray-300">
                  Military-grade encryption, advanced fraud prevention, and full chargeback
                  protection for merchants
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Customer Experience Section */}
      <motion.section
        className="px-4 py-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        <div className="mx-auto max-w-7xl">
          <motion.h2
            className="mb-4 text-4xl font-bold text-center text-pexi-orange"
            variants={fadeInUp}
          >
            Seamless Payments For Your Customers
          </motion.h2>
          <motion.p
            className="max-w-3xl mx-auto mb-16 font-light text-center text-gray-300"
            variants={fadeInUp}
          >
            Provide your customers with a frictionless payment experience while you enjoy fast,
            reliable settlements.
          </motion.p>
          <motion.div className="grid gap-8 md:grid-cols-3" variants={staggerContainer}>
            <motion.div className="text-center" variants={fadeInUp}>
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-orange-500/20 rounded-2xl">
                  <CheckCircle className="w-12 h-12 text-orange-400" />
                </div>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">No KYC Required</h3>
              <p className="text-gray-300">
                Your customers can complete payments without intrusive KYC processes or creating
                accounts, improving conversion rates.
              </p>
            </motion.div>

            <motion.div className="text-center" variants={fadeInUp}>
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-orange-500/20 rounded-2xl">
                  <Zap className="w-12 h-12 text-orange-400" />
                </div>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Zero Integration Friction</h3>
              <p className="text-gray-300">
                Simple API integration that works with your existing checkout flow, requiring
                minimal development resources.
              </p>
            </motion.div>

            <motion.div className="text-center" variants={fadeInUp}>
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-orange-500/20 rounded-2xl">
                  <TrendingUp className="w-12 h-12 text-orange-400" />
                </div>
              </div>
              <h3 className="mb-4 text-xl font-semibold text-white">Transparent Transactions</h3>
              <p className="text-gray-300">
                Complete visibility into every transaction with detailed reporting and audit trails.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Testimonials Section */}

      {/* CTA Section */}
      <motion.section
        className="px-4 py-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={fadeInUp}
      >
        <div className="mx-auto max-w-7xl">
          <motion.div
            className="p-12 text-center bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl"
            variants={fadeInUp}
          >
            <h2 className="mb-6 text-4xl font-bold text-white">
              Ready to Start Accepting Crypto Payments?
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-xl text-orange-100">
              Join thousands of merchants already using PexiBlocks for instant crypto settlements
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-orange-600 transition-colors bg-white rounded-lg hover:bg-gray-100"
                >
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-orange-600 transition-colors bg-white rounded-lg hover:bg-gray-100"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>

                </>
              )}
            </div>
          </motion.div>
        </div>
      </motion.section>
    </motion.div>
  )
}
